import { useLanguageAwareRouting } from '@gofranz/common-components';
import { NavLink, type NavLinkStylesNames, Text } from '@mantine/core';
import {
  IconCreditCard,
  IconHelp,
  IconLogout,
  IconMessage,
  IconSettings,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useRustyState } from '../../state';

const ACTIVE_NAVLINK_STYLES: Partial<Record<NavLinkStylesNames, React.CSSProperties>> = {
  root: {
    backgroundColor: 'var(--mantine-color-body)',
    border: '1px solid var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-md)',
    boxShadow: 'var(--mantine-shadow-xs)',
    position: 'relative',
    zIndex: 1,
  },
  label: {
    color: 'var(--mantine-primary-color-filled)',
    fontWeight: 600,
  },
  section: {
    color: 'var(--mantine-primary-color-filled)',
  },
};

const stylesIfActive = (active: boolean) => (active ? ACTIVE_NAVLINK_STYLES : undefined);

export function AccountNavigation() {
  const { t } = useTranslation();
  const { createLanguageURL, isActive } = useLanguageAwareRouting();

  const logout = async () => {
    await useRustyState.getState().logout();
    window.location.replace(createLanguageURL('/'));
  };

  const sectionLabelProps = {
    tt: 'uppercase' as const,
    size: 'xs' as const,
    fw: 600,
    c: 'dimmed',
    pl: 'xs',
    mb: 4,
  };

  return (
    <>
      <Text {...sectionLabelProps}>{t('glob_navigation.sectionWorkspace')}</Text>
      <NavLink
        component={Link}
        to={createLanguageURL('/chat')}
        label={t('glob_navigation.chat')}
        leftSection={<IconMessage size="18" stroke={1.5} />}
        active={isActive('/chat', true)}
        styles={stylesIfActive(isActive('/chat', true))}
      />

      <Text {...sectionLabelProps} mt="md">
        {t('glob_navigation.sectionAccount')}
      </Text>
      <NavLink
        component={Link}
        to={createLanguageURL('/account/billing/usage')}
        label={t('glob_navigation.usageAndCredits')}
        leftSection={<IconCreditCard size="18" stroke={1.5} />}
        active={isActive('/account/billing/usage', true)}
        styles={stylesIfActive(isActive('/account/billing/usage', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/profile')}
        label={t('glob_navigation.profile')}
        leftSection={<IconSettings size="18" stroke={1.5} />}
        active={isActive('/account/profile', true)}
        styles={stylesIfActive(isActive('/account/profile', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/security-log')}
        label={t('glob_navigation.securityLog')}
        leftSection={<IconShieldCheck size="18" stroke={1.5} />}
        active={isActive('/account/security-log', true)}
        styles={stylesIfActive(isActive('/account/security-log', true))}
      />

      <Text {...sectionLabelProps} mt="md">
        {t('glob_navigation.sectionHelp')}
      </Text>
      <NavLink
        component={Link}
        to={createLanguageURL('/account/support')}
        label={t('glob_navigation.support')}
        leftSection={<IconHelp size="18" stroke={1.5} />}
        active={isActive('/account/support', true)}
        styles={stylesIfActive(isActive('/account/support', true))}
      />

      <NavLink
        onClick={logout}
        href="#"
        label={t('glob_navigation.logout')}
        leftSection={<IconLogout size="18" stroke={1.5} />}
      />
    </>
  );
}
